import classNames from 'classnames';
import React, { useState } from 'react';
import type { DragStart, DropResult } from 'react-beautiful-dnd';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import SVGIcon from '../../components/SVGIcon';
import dragHandle from '../../icons/drag.svg';
import styles from './TopicReorderablePosts.module.scss';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';

const POST_EDGES_FRAGMENT = gql(`
  fragment TopicReorderablePosts_postEdges on TopicPostEdge {
    node {
      id
    }
    pinnedOrder
  }
`);

type PostEdgesFragment = DocumentType<typeof POST_EDGES_FRAGMENT>;

interface TopicReorderablePostsProps<P extends PostEdgesFragment> {
  postEdges: P[];
  orderPost(id: string, newOrder: number): Promise<unknown>;
  renderPostEdge(postEdge: P, index: number): JSX.Element;
}

const TopicReorderablePosts = <P extends PostEdgesFragment>({
  postEdges,
  orderPost,
  renderPostEdge,
}: TopicReorderablePostsProps<P>) => {
  const [draggingPostId, setDraggingPostId] = useState<string | null>(null);
  const [overridePostEdges, setOverridePostEdges] = useState<P[] | null>(null);
  const onDragStart = (initial: DragStart) => {
    setDraggingPostId(initial.draggableId);
  };

  const onDragEnd = ({ source, destination }: DropResult) => {
    setDraggingPostId(null);
    if (!destination || destination.index === source.index) {
      return;
    }

    const oldIndex = source.index;
    const newIndex = destination.index;
    const newPostEdges = postEdges.slice();
    const [edge] = newPostEdges.splice(oldIndex, 1);

    const newOrder = (() => {
      const postAtNewIndex = newPostEdges[newIndex];
      if (postAtNewIndex) {
        return postAtNewIndex.pinnedOrder;
      }

      const postAtLastIndex = newPostEdges[newPostEdges.length - 1];

      if (postAtLastIndex?.pinnedOrder != null) {
        return postAtLastIndex.pinnedOrder + 1;
      }

      return undefined;
    })();

    if (edge?.node && newOrder != null) {
      newPostEdges.splice(newIndex, 0, edge);
      setOverridePostEdges(newPostEdges);

      orderPost(edge.node.id, newOrder).then(() => {
        setOverridePostEdges(null);
      });
    }
  };

  return (
    <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <Droppable droppableId="topic-pinned-post-droppable">
        {({ droppableProps, innerRef: droppableRef, placeholder }) => (
          <div ref={droppableRef} {...droppableProps}>
            {(overridePostEdges ?? postEdges).map((edge, index) => {
              const post = edge.node;
              if (post == null) return null;

              return (
                <Draggable key={post.id} draggableId={post.id} index={index}>
                  {({
                    draggableProps,
                    dragHandleProps,
                    innerRef: draggableRef,
                  }) => (
                    <div
                      className={classNames(styles.draggableContainer, {
                        // :hover is not true for item that is being active dragged,
                        // while dragging, :hover could be triggered on other elements,
                        // need extra classes to control the states.
                        [styles.draggable]: !draggingPostId,
                        [styles.dragging]: draggingPostId === post.id,
                      })}
                      ref={draggableRef}
                      {...draggableProps}
                      {...dragHandleProps}
                    >
                      <div className={styles.dragHandleContainer}>
                        <SVGIcon coloringWith="fill">{dragHandle}</SVGIcon>
                      </div>
                      {renderPostEdge(edge, index)}
                    </div>
                  )}
                </Draggable>
              );
            })}
            {placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default TopicReorderablePosts;
